import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminOnly from '../../components/AdminOnly';
import EmployeeAvatar from '../../components/EmployeeAvatar';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Avatar,
  Drawer,
  Descriptions,
  Select,
  Modal,
  message,
  AutoComplete,
  Spin,
  Empty,
} from 'antd';
import {
  UserOutlined,
  EyeOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
  SortAscendingOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  UserAddOutlined,
  EditOutlined,
  SaveOutlined,
  PlusOutlined,
  UploadOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { fetchEmployeeList } from '../../redux/slices/employeeSlice';
import { formatDateTime } from '../../utils/helpers';
import api from '../../utils/api';
import { usePhotoRefresh } from '../../context/PhotoRefreshContext';

const { Title, Text } = Typography;
const { Option } = Select;

const StyledCard = styled(Card)`
  border-radius: 8px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0f0f0;
`;

const TableWrapper = styled.div`
  .ant-table-body {
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: transparent;
      border-radius: 4px;
    }

    &:hover::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 0, 0, 0.3);
    }
  }
`;

const SearchWrapper = styled.div`
  .ant-select-auto-complete {
    .ant-select-selector {
      border-radius: 12px !important;
      border: 2px solid #e8e8e8 !important;
      padding: 8px 16px !important;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      height: 48px !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
      background: #ffffff;

      &:hover {
        border-color: #B4A0F7 !important;
        box-shadow: 0 4px 12px rgba(180, 160, 247, 0.15) !important;
      }

      .ant-select-selection-search-input {
        font-size: 15px;
        height: 100% !important;

        &::placeholder {
          color: #a0a0a0;
          font-weight: 400;
        }
      }

      .ant-select-selection-placeholder {
        color: #a0a0a0;
        font-size: 15px;
      }
    }

    &.ant-select-focused .ant-select-selector {
      border-color: #B4A0F7 !important;
      box-shadow: 0 4px 16px rgba(180, 160, 247, 0.25) !important;
    }
  }
`;

const PhotoGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  margin-top: 16px;
`;

const PhotoItem = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid ${props => props.$selected ? '#B4A0F7' : 'transparent'};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:hover .delete-button {
    opacity: 1;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${props => props.$selected && `
    &::after {
      content: '✓';
      position: absolute;
      top: 4px;
      right: 4px;
      background: #B4A0F7;
      color: white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 14px;
      z-index: 1;
    }
  `}
`;

const DeleteButton = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgba(255, 77, 79, 0.9);
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;

  &:hover {
    background: rgba(255, 77, 79, 1);
    transform: scale(1.1);
  }

  .anticon {
    font-size: 14px;
  }
`;

const AddPhotoSlot = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  border-radius: 8px;
  border: 2px dashed #d9d9d9;
  background: #fafafa;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #B4A0F7;
    background: #f5f3ff;
    transform: scale(1.05);
  }

  .add-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: #8c8c8c;
  }

  .anticon {
    font-size: 32px;
    color: #B4A0F7;
    display: block;
    margin-bottom: 8px;
  }

  span {
    font-size: 12px;
  }
`;

const SkeletonPhotoItem = styled.div`
  width: 100%;
  padding-bottom: 100%;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    110deg,
    #f0f0f0 8%,
    #f8f8f8 18%,
    #f0f0f0 33%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s linear infinite;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  /* Pulse overlay for more dynamic effect */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #B4A0F7 0%, #9b7fef 100%);
    opacity: 0.2;
    animation: pulse 2s ease-in-out infinite;
  }

  /* Loading icon */
  &::after {
    content: '📸';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 24px;
    animation: iconBounce 1.5s ease-in-out infinite;
    filter: grayscale(0.3);
  }

  @keyframes pulse {
    0%, 100% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0.2;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.5);
      opacity: 0.1;
    }
  }

  @keyframes iconBounce {
    0%, 100% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0.5;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.1);
      opacity: 0.8;
    }
  }
`;

const Employees = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { list, loading, total } = useSelector((state) => state.employee);
  const { refreshEmployeePhoto } = usePhotoRefresh();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('last_seen');
  const [detailDrawer, setDetailDrawer] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeePhotos, setEmployeePhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [editImageModal, setEditImageModal] = useState(false);
  const [savingPrimaryPhoto, setSavingPrimaryPhoto] = useState(false);
  const [currentDisplayedEmpId, setCurrentDisplayedEmpId] = useState(null);
  const [photoLoadVersion, setPhotoLoadVersion] = useState(0); // Increment to force reset
  const [isModalContentReady, setIsModalContentReady] = useState(false); // Control when to render modal content
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  // Autocomplete state
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [searchingEmployees, setSearchingEmployees] = useState(false);

  // Debounce timer refs
  const debounceTimer = useRef(null);
  const suggestionDebounceTimer = useRef(null);
  const isSearchingRef = useRef(false);
  const lastSearchValue = useRef('');
  const currentLoadingEmpIdRef = useRef(null); // Track which employee is currently loading

  // Load data function for manual refresh after operations
  const loadData = useCallback(() => {
    dispatch(
      fetchEmployeeList({
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search: searchText || undefined,
        order_by: sortBy,
      })
    );
  }, [dispatch, page, pageSize, searchText, sortBy]);

  // Load data - fetch employee list on mount and when params change
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Debounced search function
  const debouncedSearch = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setPage(1);
      // Don't need to dispatch here since setPage will trigger the useEffect above
    }, 500); // 500ms delay
  }, []);

  const handleSearchSuggestions = (searchValue) => {
    // Clear previous timer
    if (suggestionDebounceTimer.current) {
      clearTimeout(suggestionDebounceTimer.current);
    }

    if (!searchValue || searchValue.length < 2) {
      setEmployeeSuggestions([]);
      lastSearchValue.current = '';
      return;
    }

    // Skip if already searching or same value
    if (isSearchingRef.current || lastSearchValue.current === searchValue) {
      return;
    }

    // Debounce the search to reduce API calls
    suggestionDebounceTimer.current = setTimeout(async () => {
      // Double check before making API call
      if (isSearchingRef.current || lastSearchValue.current === searchValue) {
        return;
      }

      try {
        isSearchingRef.current = true;
        lastSearchValue.current = searchValue;
        setSearchingEmployees(true);

        const result = await api.getEmployeeList({
          search: searchValue,
          limit: 10,
          offset: 0,
        });

        // Filter results to only include employees that actually match the search term
        const searchLower = searchValue.toLowerCase();
        const filteredData = result.data.filter((emp) => {
          const empIdMatch = emp.emp_id?.toLowerCase().includes(searchLower);
          const nameMatch = emp.full_name?.toLowerCase().includes(searchLower);
          const emailMatch = emp.email?.toLowerCase().includes(searchLower);
          return empIdMatch || nameMatch || emailMatch;
        });

        // Limit to top 5 matches
        const topMatches = filteredData.slice(0, 5);

        const suggestions = topMatches.map((emp) => ({
          value: emp.emp_id,
          label: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <Avatar size="small" icon={<UserOutlined />} style={{ background: '#B4A0F7' }} />
                <div>
                  <div><Text strong>{emp.emp_id}</Text> - {emp.full_name}</div>
                  {emp.email && <Text type="secondary" style={{ fontSize: 12 }}>{emp.email}</Text>}
                </div>
              </Space>
              {emp.department && <Tag color="blue">{emp.department}</Tag>}
            </div>
          ),
        }));

        setEmployeeSuggestions(suggestions);
      } catch (error) {
        console.error('Error searching employees:', error);
        setEmployeeSuggestions([]);
      } finally {
        setSearchingEmployees(false);
        isSearchingRef.current = false;
      }
    }, 400); // 400ms delay to reduce API calls while typing
  };

  const showDetail = async (record) => {
    const empId = record.emp_id;

    // FORCE CLOSE MODALS FIRST to prevent any preview of old data
    setEditImageModal(false);

    // Set ref FIRST (synchronous)
    currentLoadingEmpIdRef.current = empId;

    // AGGRESSIVE STATE CLEARING - Clear ALL employee data immediately
    setEmployeePhotos([]);
    setSelectedPhoto(null);
    setSelectedEmployee(null);
    setCurrentDisplayedEmpId(null);

    // Wait 50ms to ensure state fully clears before loading new data
    await new Promise(resolve => setTimeout(resolve, 50));

    // Increment version to force complete reset of photo gallery
    setPhotoLoadVersion(prev => prev + 1);

    // NOW set the new employee ID
    setCurrentDisplayedEmpId(empId);
    setLoadingPhotos(true);

    // Then set the new employee and open drawer
    setSelectedEmployee(record);
    setDetailDrawer(true);

    try {
      const response = await api.getEmployeePhotos(empId);

      // Only update if still current employee (prevent race conditions)
      if (currentLoadingEmpIdRef.current !== empId) {
        console.log(`Ignoring stale response for ${empId}`);
        return;
      }

      // Pre-compute photo URLs with cache-busting timestamp
      const timestamp = Date.now();
      const photosWithUrls = (response.photos || []).map(photo => ({
        filename: photo,
        url: api.getEmployeePhoto(empId, photo, timestamp) // Use API function properly
      }));
      setEmployeePhotos(photosWithUrls);
      // If employee has a primary photo, use it; otherwise select the first photo
      if (record.primary_photo) {
        setSelectedPhoto(record.primary_photo);
      } else if (response.photos && response.photos.length > 0) {
        setSelectedPhoto(response.photos[0]);
      }
    } catch (error) {
      console.error('Error loading employee photos:', error);
      message.error('Failed to load employee photos');
    } finally {
      // Only clear loading if still current employee
      if (currentLoadingEmpIdRef.current === empId) {
        setLoadingPhotos(false);
      }
    }
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setPage(1);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Employee',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete employee ${record.emp_id} (${record.full_name})? This will remove all photos and logs permanently.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.deleteEmployee(record.emp_id);
          message.success(`Successfully deleted ${record.emp_id}`);
          loadData();
        } catch (error) {
          message.error(error.message || 'Failed to delete');
        }
      },
    });
  };

  const handleEditImage = async () => {
    if (!selectedEmployee) return;

    const empId = selectedEmployee.emp_id;
    const employeeData = { ...selectedEmployee }; // Snapshot current employee data

    // STEP 1: IMMEDIATELY close modal and clear ALL state
    if (editImageModal) {
      setEditImageModal(false);
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    // Clear everything including selectedEmployee to prevent showing old data
    setEmployeePhotos([]);
    setSelectedPhoto(null);
    setCurrentDisplayedEmpId(null);
    setIsModalContentReady(false);
    setSelectedEmployee(null); // ← CRITICAL: Clear selected employee
    currentLoadingEmpIdRef.current = null;

    // Wait for React to process state updates
    await new Promise(resolve => setTimeout(resolve, 50));

    // STEP 2: Set tracking refs
    currentLoadingEmpIdRef.current = empId;
    setPhotoLoadVersion(prev => prev + 1);
    setCurrentDisplayedEmpId(empId);
    setLoadingPhotos(true);

    // STEP 3: Fetch photos BEFORE opening modal
    try {
      const response = await api.getEmployeePhotos(empId);

      // Check if still current employee
      if (currentLoadingEmpIdRef.current !== empId) {
        return;
      }

      // Pre-compute photo URLs with cache-busting
      const timestamp = Date.now();
      const photosWithUrls = (response.photos || []).map(photo => ({
        filename: photo,
        url: api.getEmployeePhoto(empId, photo, timestamp)
      }));

      // Set selected photo using the snapshot data
      const primaryPhoto = employeeData.primary_photo || (response.photos && response.photos.length > 0 ? response.photos[0] : null);

      // CRITICAL: Update ALL state atomically in one render cycle
      // This ensures modal opens with complete, correct data
      setEmployeePhotos(photosWithUrls);
      setSelectedPhoto(primaryPhoto);
      setSelectedEmployee(employeeData); // ← Restore employee data with correct employee
      setIsModalContentReady(true);

      // Small delay to ensure state is committed
      await new Promise(resolve => setTimeout(resolve, 10));

      // STEP 4: NOW open the modal - everything is ready
      setEditImageModal(true);
    } catch (error) {
      console.error('Error loading employee photos:', error);
      message.error('Failed to load employee photos');
      // Restore employee data on error
      setSelectedEmployee(employeeData);
    } finally {
      if (currentLoadingEmpIdRef.current === empId) {
        setLoadingPhotos(false);
      }
    }
  };

  const getPhotoUrl = (filename) => {
    // SAFETY: Only use currentDisplayedEmpId to prevent showing wrong employee photos
    const empId = currentDisplayedEmpId || selectedEmployee?.emp_id;
    if (!empId) return null;

    if (!filename) return api.getEmployeePhoto(empId);
    const photo = employeePhotos.find(p => p.filename === filename);
    return photo ? photo.url : api.getEmployeePhoto(empId, filename);
  };

  const handleSavePrimaryPhoto = async () => {
    if (!selectedPhoto || !selectedEmployee) {
      message.error('Please select a photo first');
      return;
    }

    setSavingPrimaryPhoto(true);
    try {
      await api.setPrimaryPhoto(selectedEmployee.emp_id, selectedPhoto);
      message.success('Primary photo updated successfully!');

      // Update the selected employee's primary_photo field
      setSelectedEmployee({
        ...selectedEmployee,
        primary_photo: selectedPhoto
      });

      // Trigger photo refresh to update avatars
      refreshEmployeePhoto(selectedEmployee.emp_id);

      setEditImageModal(false);
      loadData(); // Reload to update the list
    } catch (error) {
      message.error(error.message || 'Failed to update primary photo');
    } finally {
      setSavingPrimaryPhoto(false);
    }
  };

  const handleAddPhotos = () => {
    if (!selectedEmployee) return;
    if (employeePhotos.length >= 20) {
      message.warning('Maximum 20 photos allowed');
      return;
    }
    document.getElementById('add-employee-photos-input').click();
  };

  const handlePhotoFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (employeePhotos.length + files.length > 20) {
      message.error(`Can only add ${20 - employeePhotos.length} more photo(s). Maximum 20 photos allowed.`);
      return;
    }

    setUploadingPhotos(true);
    try {
      const formData = new FormData();

      // Add validation and append valid files
      let validFileCount = 0;
      for (const file of files) {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
          message.error(`${file.name} is not an image file!`);
          continue;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
          message.error(`${file.name} must be smaller than 5MB!`);
          continue;
        }
        formData.append('images', file);
        validFileCount++;
      }

      if (validFileCount === 0) {
        message.error('No valid images to upload');
        return;
      }

      // Upload additional photos using the new endpoint
      await api.addEmployeePhotos(selectedEmployee.emp_id, formData);
      message.success(`Successfully added ${validFileCount} photo(s)`);

      // Refresh photo list
      const response = await api.getEmployeePhotos(selectedEmployee.emp_id);
      const timestamp = Date.now();
      const photosWithUrls = (response.photos || []).map(photo => ({
        filename: photo,
        url: api.getEmployeePhoto(selectedEmployee.emp_id, photo, timestamp)
      }));
      setEmployeePhotos(photosWithUrls);

      // Trigger photo refresh to update avatars
      refreshEmployeePhoto(selectedEmployee.emp_id);
      loadData();
    } catch (error) {
      message.error(error.message || 'Failed to add photos');
    } finally {
      setUploadingPhotos(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleDeletePhoto = (filename) => {
    if (!selectedEmployee || employeePhotos.length <= 1) {
      message.error('Cannot delete photo. Employee must have at least 1 photo.');
      return;
    }

    Modal.confirm({
      title: 'Delete Photo',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this photo?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.deleteEmployeePhoto(selectedEmployee.emp_id, filename);
          message.success('Photo deleted successfully');

          // Remove from local state
          const updatedPhotos = employeePhotos.filter(p => p.filename !== filename);
          setEmployeePhotos(updatedPhotos);

          // If deleted photo was selected, select first remaining photo
          if (selectedPhoto === filename) {
            setSelectedPhoto(updatedPhotos.length > 0 ? updatedPhotos[0].filename : null);
          }

          // Trigger photo refresh
          refreshEmployeePhoto(selectedEmployee.emp_id);
          loadData();
        } catch (error) {
          message.error(error.message || 'Failed to delete photo');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'emp_id',
      key: 'emp_id',
      render: (text, record) => (
        <Space>
          <EmployeeAvatar
            empId={record.emp_id}
            size={40}
            enablePreview={true}
            photoUpdatedAt={record.photo_updated_at}
            style={{ background: 'linear-gradient(135deg, #B4A0F7 0%, #B4A0F7 100%)' }}
          />
          <div>
            <div><Text style={{ fontWeight: 500 }}>{record.emp_id}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.full_name}</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (text) => text ? (
        <Tag
          bordered
          color="blue"
          style={{
            borderRadius: '4px',
            fontSize: '12px',
            padding: '2px 8px',
            fontWeight: 500
          }}
        >
          {text}
        </Tag>
      ) : <Text type="secondary">-</Text>,
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Last Seen',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (text) => (
        <span style={{ color: '#6B7280', fontSize: '13px' }}>
          {formatDateTime(text)}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (text) => (
        <Space size={6}>
          <span style={{
            display: 'inline-block',
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: text === 'Y' ? '#10B981' : '#9CA3AF'
          }} />
          <span style={{ color: '#374151', fontSize: '13px' }}>
            {text === 'Y' ? 'Active' : 'Inactive'}
          </span>
        </Space>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size={4}>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => showDetail(record)}
            style={{ color: '#6366F1' }}
          />
          <AdminOnly>
            <Button
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
              style={{ color: '#6B7280' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
            />
          </AdminOnly>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12 }}>
        <AdminOnly>
          <Button
            type="primary"
            size="large"
            icon={<UserAddOutlined />}
            onClick={() => navigate('/employees/register')}
          >
            Register New Employee
          </Button>
        </AdminOnly>
      </div>

      <StyledCard>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <SearchWrapper style={{ flex: 1, minWidth: 280, maxWidth: 500 }}>
              <AutoComplete
                value={searchText}
                options={employeeSuggestions}
                onSearch={handleSearchSuggestions}
                onSelect={(value) => {
                  setSearchText(value);
                  if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                  }
                  if (suggestionDebounceTimer.current) {
                    clearTimeout(suggestionDebounceTimer.current);
                  }
                  setPage(1);
                }}
                onChange={(value) => {
                  setSearchText(value);
                  debouncedSearch();
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    if (debounceTimer.current) {
                      clearTimeout(debounceTimer.current);
                    }
                    if (suggestionDebounceTimer.current) {
                      clearTimeout(suggestionDebounceTimer.current);
                    }
                    setPage(1);
                  }
                }}
                placeholder="Search by ID, name, or email..."
                style={{ width: '100%' }}
                size="large"
                notFoundContent={searchingEmployees ? <Spin size="small" /> : null}
                popupMatchSelectWidth={500}
                allowClear
                onClear={() => {
                  setSearchText('');
                  setPage(1);
                }}
              />
            </SearchWrapper>
            <Space wrap>
              <Select
                value={sortBy}
                onChange={handleSortChange}
                style={{ width: 200 }}
                size="large"
                prefix={<SortAscendingOutlined />}
              >
                <Option value="last_seen">Latest Seen</Option>
                <Option value="first_seen">First Seen</Option>
                <Option value="date_added">Date Added</Option>
                <Option value="emp_id">ID (A-Z)</Option>
                <Option value="full_name">Name (A-Z)</Option>
                <Option value="department">Department</Option>
              </Select>
              <Tag color="blue" style={{ fontSize: 16, padding: '8px 16px', borderRadius: '8px' }}>
                {total} Employees
              </Tag>
            </Space>
          </div>

          <TableWrapper>
            <Table
              size="small"
              columns={columns}
              dataSource={list}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1200, y: 'calc(100vh - 380px)' }}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} employees`,
                onChange: (page, pageSize) => {
                  setPage(page);
                  setPageSize(pageSize);
                },
              }}
            />
          </TableWrapper>
        </Space>
      </StyledCard>

      {/* Detail Drawer */}
      <Drawer
        title="Employee Details"
        placement="right"
        width={500}
        onClose={() => setDetailDrawer(false)}
        open={detailDrawer}
      >
        {selectedEmployee && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <EmployeeAvatar
                empId={selectedEmployee.emp_id}
                size={80}
                enablePreview={true}
                photoUpdatedAt={selectedEmployee.photo_updated_at}
                style={{ background: 'linear-gradient(135deg, #B4A0F7 0%, #B4A0F7 100%)' }}
              />
              <Title level={4} style={{ marginTop: 16 }}>
                {selectedEmployee.full_name}
              </Title>
              <Text type="secondary">{selectedEmployee.emp_id}</Text>
              {isAdmin() && (
                <div style={{ marginTop: 12 }}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={handleEditImage}
                    size="small"
                  >
                    Edit Image
                  </Button>
                </div>
              )}
            </div>

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Email">
                {selectedEmployee.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Department">
                {selectedEmployee.department || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Division">
                {selectedEmployee.division || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Team">
                {selectedEmployee.team || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Role">
                {selectedEmployee.role_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedEmployee.phone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedEmployee.active === 'Y' ? 'green' : 'default'}>
                  {selectedEmployee.active === 'Y' ? 'Active' : 'Inactive'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Card title="Last Activity" size="small">
              <div style={{ padding: '16px 0' }}>
                <Space style={{ marginBottom: 12, width: '100%' }}>
                  <ClockCircleOutlined style={{ fontSize: 16, color: '#1890ff' }} />
                  <div>
                    <Text strong>Last Seen</Text>
                    <br />
                    <Text type="secondary">
                      {formatDateTime(selectedEmployee.last_seen)}
                    </Text>
                  </div>
                </Space>
                <Space style={{ marginBottom: 12, width: '100%' }}>
                  <EnvironmentOutlined style={{ fontSize: 16, color: '#52c41a' }} />
                  <div>
                    <Text strong>Location</Text>
                    <br />
                    <Text type="secondary">
                      {selectedEmployee.last_crossing_zone || 'Unknown'}
                    </Text>
                  </div>
                </Space>
                <Space style={{ width: '100%' }}>
                  <ClockCircleOutlined style={{ fontSize: 16, color: '#8c8c8c' }} />
                  <div>
                    <Text strong>First Seen</Text>
                    <br />
                    <Text type="secondary">
                      {formatDateTime(selectedEmployee.first_seen)}
                    </Text>
                  </div>
                </Space>
              </div>
            </Card>
          </Space>
        )}
      </Drawer>

      {/* Edit Image Modal */}
      <Modal
        key={`modal-${photoLoadVersion}-${currentDisplayedEmpId || 'no-employee'}`}
        title="Edit Employee Image"
        open={editImageModal}
        onCancel={() => {
          setEditImageModal(false);
          setIsModalContentReady(false);
          setEmployeePhotos([]);
          setSelectedPhoto(null);
          setCurrentDisplayedEmpId(null);
          currentLoadingEmpIdRef.current = null;
        }}
        destroyOnClose={true}
        maskClosable={false}
        footer={[
          <Button key="cancel" onClick={() => {
            setEditImageModal(false);
            setIsModalContentReady(false);
            setEmployeePhotos([]);
            setSelectedPhoto(null);
            setCurrentDisplayedEmpId(null);
            currentLoadingEmpIdRef.current = null;
          }}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            loading={savingPrimaryPhoto}
            onClick={handleSavePrimaryPhoto}
            disabled={!isModalContentReady}
          >
            Save as Primary
          </Button>,
        ]}
        width={600}
      >
        {/* Show loading if IDs don't match OR content not ready */}
        {!currentDisplayedEmpId || !selectedEmployee || currentDisplayedEmpId !== selectedEmployee.emp_id || !isModalContentReady ? (
          <div style={{
            textAlign: 'center',
            padding: '80px 40px',
            background: 'linear-gradient(135deg, #f8f7ff 0%, #ffffff 100%)',
            borderRadius: '12px'
          }}>
            <Spin size="large" tip={
              <span style={{
                display: 'block',
                marginTop: 16,
                color: '#B4A0F7',
                fontSize: 16,
                fontWeight: 500
              }}>
                Loading employee photos...
              </span>
            } />
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            {/* Actual content - only renders when everything is ready */}
          <div key={`employee-content-${photoLoadVersion}-${selectedEmployee.emp_id}-${currentDisplayedEmpId}`}>
            <div style={{ textAlign: 'center', marginBottom: 12 }}>
              <Avatar
                key={`modal-${photoLoadVersion}-${selectedEmployee.emp_id}-${selectedPhoto || 'default'}`}
                size={120}
                src={getPhotoUrl(selectedPhoto)}
                icon={<UserOutlined />}
                style={{ background: 'linear-gradient(135deg, #B4A0F7 0%, #B4A0F7 100%)' }}
              />
              <Title level={5} style={{ marginTop: 16, marginBottom: 4 }}>
                {selectedEmployee.full_name}
              </Title>
              <Text type="secondary">{selectedEmployee.emp_id}</Text>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Text strong>
                Select a photo to set as primary ({employeePhotos.length}/20):
              </Text>
              {employeePhotos.length < 20 && (
                <Button
                  size="small"
                  icon={<UploadOutlined />}
                  onClick={handleAddPhotos}
                  loading={uploadingPhotos}
                >
                  Add Photos
                </Button>
              )}
            </div>

            {/* Hidden file input */}
            <input
              id="add-employee-photos-input"
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handlePhotoFileChange}
            />

            <div key={`photo-container-${photoLoadVersion}-${currentDisplayedEmpId}`}>
              {loadingPhotos || uploadingPhotos ? (
                // Show skeleton loaders while photos are loading
                <PhotoGallery>
                  {[...Array(6)].map((_, index) => (
                    <SkeletonPhotoItem key={`skeleton-${index}`} />
                  ))}
                </PhotoGallery>
              ) : employeePhotos.length > 0 ? (
                <PhotoGallery key={`photos-${photoLoadVersion}-${selectedEmployee.emp_id}`}>
                  {employeePhotos.map((photo, index) => (
                    <PhotoItem
                      key={`${photoLoadVersion}-${selectedEmployee.emp_id}-${photo.filename}-${index}`}
                      $selected={selectedPhoto === photo.filename}
                      onClick={() => setSelectedPhoto(photo.filename)}
                    >
                      <img
                        key={`img-${photoLoadVersion}-${currentDisplayedEmpId}-${photo.filename}`}
                        src={photo.url}
                        alt={`${currentDisplayedEmpId}-${index}`}
                        onError={(e) => {
                          console.error(`Failed to load: ${photo.url}`);
                          e.target.src = 'https://via.placeholder.com/100x100?text=Error';
                        }}
                      />
                      {employeePhotos.length > 1 && (
                        <DeleteButton
                          className="delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePhoto(photo.filename);
                          }}
                        >
                          <CloseCircleOutlined />
                        </DeleteButton>
                      )}
                    </PhotoItem>
                  ))}
                  {/* Single Add photo slot if under limit */}
                  {employeePhotos.length < 20 && (
                    <AddPhotoSlot key="add-slot" onClick={handleAddPhotos}>
                      <div className="add-content">
                        <PlusOutlined />
                        <span>Add Photo</span>
                      </div>
                    </AddPhotoSlot>
                  )}
                </PhotoGallery>
              ) : (
                <Empty description="No photos available" />
              )}
            </div>
          </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Employees;
